import React from 'react';
import { handleRedirect, acquireGraphAccessToken } from '../auth/msal';
import type { Account, AccountState, Profile } from '@shared/types/account';

type SessionStatus = 'checking' | 'unauth' | 'auth';

export type SessionValue = {
  status: SessionStatus;
  profile: Profile | null;
  account: Account | null;
  accountState: AccountState | null;
  autoActivateEnabled: boolean;
  isSignedUp: boolean;
  isWaitlisted: boolean;
  isActive: boolean;
  isSystemAdmin: boolean;
  hasPhone: boolean;
  hasBilling: boolean;
  refresh: () => Promise<void>;
  markRegistered: (account: Account) => void;
  updateAccount: (account: Account | null) => void;
};

const SessionContext = React.createContext<SessionValue | undefined>(undefined);

function deriveAccountState(acc: Account | null): AccountState | null {
  if (!acc) return null;
  if (acc.state === 'signedUp' || acc.state === 'waitlisted' || acc.state === 'active') return acc.state;
  if (acc.businessInfo) return 'waitlisted';
  return 'signedUp';
}

function deriveHasBilling(acc: Account | null): boolean {
  if (!acc?.billing) return false;
  return acc.billing.status === 'active' && Boolean(acc.billing.subscriptionId);
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<SessionStatus>('checking');
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const [account, setAccount] = React.useState<Account | null>(null);
  const [accountState, setAccountState] = React.useState<AccountState | null>(null);
  const [autoActivateEnabled, setAutoActivateEnabled] = React.useState(true);
  const [hasPhone, setHasPhone] = React.useState(false);
  const [hasBilling, setHasBilling] = React.useState(false);
  const bootstrappedRef = React.useRef(false);

  const applyAccount = React.useCallback((nextAccount: Account | null) => {
    setAccount(nextAccount);
    const derivedState = deriveAccountState(nextAccount);
    setAccountState(derivedState);
    setHasPhone(Boolean(nextAccount?.phoneNumber));
    setHasBilling(deriveHasBilling(nextAccount));
  }, []);

  const refresh = React.useCallback(async () => {
    try {
      const me = await fetch('/api/me', { credentials: 'include', cache: 'no-store' });
      if (me.status !== 200) {
        setStatus('unauth');
        setProfile(null);
        applyAccount(null);
        setAutoActivateEnabled(true);
        return;
      }
      const meData = await me.json().catch(() => ({}));
      setProfile(meData.profile || null);

      const accResp = await fetch('/api/accounts/me', { credentials: 'include', cache: 'no-store' });
      if (accResp.ok) {
        const accData = await accResp.json().catch(() => ({}));
        const acc = (accData.account || null) as Account | null;
        applyAccount(acc);
        setAutoActivateEnabled(typeof accData.autoActivateEnabled === 'boolean' ? accData.autoActivateEnabled : true);
      } else {
        applyAccount(null);
        setAutoActivateEnabled(true);
      }
      setStatus('auth');
    } catch {
      setStatus('unauth');
      setProfile(null);
      applyAccount(null);
      setAutoActivateEnabled(true);
    }
  }, [applyAccount]);

  const updateAccount = React.useCallback((nextAccount: Account | null) => {
    applyAccount(nextAccount);
    if (nextAccount) setStatus('auth');
  }, [applyAccount]);

  const markRegistered = React.useCallback((registeredAccount: Account) => {
    updateAccount(registeredAccount);
  }, [updateAccount]);

  React.useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    (async () => {
      setStatus('checking');
      try {
        const result = await handleRedirect();
        if (result?.idToken) {
          const graphAccessToken = result.accessToken || (await acquireGraphAccessToken());
          const callbackResp = await fetch('/api/auth/callback', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ idToken: result.idToken, graphAccessToken }),
          });
          if (!callbackResp.ok) {
            setStatus('unauth');
            return;
          }
        }

        await refresh();
      } catch {
        setStatus('unauth');
        setProfile(null);
        applyAccount(null);
      }
    })();
  }, [refresh, applyAccount]);

  const value = React.useMemo<SessionValue>(() => ({
    status,
    profile,
    account,
    accountState,
    autoActivateEnabled,
    isSignedUp: accountState === 'signedUp',
    isWaitlisted: accountState === 'waitlisted',
    isActive: accountState === 'active',
    isSystemAdmin: Boolean(account?.isSystemAdmin),
    hasPhone,
    hasBilling,
    refresh,
    markRegistered,
    updateAccount,
  }), [status, profile, account, accountState, autoActivateEnabled, hasPhone, hasBilling, refresh, markRegistered, updateAccount]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionInternal() {
  const ctx = React.useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
